import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { RegionVariantService } from 'app/core/region-variant/region-variant.service';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';

/** Fixes date editing in MatDatePickerInput.
 * Default adapter does not round-trip dd/mm/yyyy dates correctly.
 * This adapter parses and formats using the locale of the currently selected RegionVariant
 *
 * Register in shared module via:
 * { provide: DateAdapter, useClass: IntlDateAdapter }
 */
@Injectable()
export class IntlDateAdapter extends NativeDateAdapter implements OnDestroy {
  private _dateFormat?: Intl.DateTimeFormat;
  private readonly unsubscribeAll$ = new Subject<void>();

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string,
    regionVariantService: RegionVariantService,
  ) {
    super(matDateLocale);
    this.setLocale(regionVariantService.getLocalisation() || matDateLocale);
    regionVariantService.changes$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe(() => this.setLocale(regionVariantService.getLocalisation()));
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  override setLocale(locale: string): void {
    this._dateFormat = undefined;
    super.setLocale(locale);
  }

  private get dateFormat(): Intl.DateTimeFormat {
    return this._dateFormat || (this._dateFormat = new Intl.DateTimeFormat(this.locale));
  }

  parse(value: any): Date | null {
    if (typeof value === 'string' && value.indexOf('/') > -1) {
      // const mo = moment(value, 'l', this.locale).toDate();
      const dt = DateTime.fromFormat(value, 'D', this.locale).toJSDate();
      // if (mo.getTime() !== dt.getTime()) { throw new Error(`Dates differ: mo=${mo} dt=${dt}`); }
      return dt;
    }
    const timestamp = typeof value === 'number' ? value : Date.parse(value);
    return isNaN(timestamp) ? null : new Date(timestamp);
  }

  /** Convert date to display format */
  format(date: Date, displayFormat: any): string {
    // displayFormat is {year: 'numeric', month: 'numeric', day: 'numeric'}
    return this.dateFormat.format(date);
  }
}
